import React from 'react';
import { motion } from 'framer-motion';
import { Tag, Gift, Zap, Clock, ChevronRight, Globe, Mail, Server, Code, Smartphone, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const Offers = () => {
  const activeOffers = [
    {
      title: 'Startup Digital Bundle',
      desc: 'Everything you need to get your business online. Includes Domain, Hosting, and a 5-page Responsive Website.',
      discount: '40% OFF',
      icon: Gift,
      color: 'from-blue-600 to-indigo-700',
      price: 'Starting at ₹19,999',
      features: ['Free Domain (.in)', '1 Year Hosting', 'Business Emails', 'Basic SEO Setup']
    },
    {
      title: 'Mobile App Special',
      desc: 'Launch your Android & iOS app with our hybrid development framework at a special introductory price.',
      discount: '30% OFF',
      icon: Smartphone,
      color: 'from-pink-600 to-rose-700',
      price: 'Starting at ₹49,999',
      features: ['Android + iOS', 'Firebase Integration', 'Push Notifications', 'Store Submission']
    },
    {
      title: 'Cloud Migration Offer',
      desc: 'Migrate your legacy systems to AWS or Google Cloud with zero downtime and expert consultation.',
      discount: 'FREE Setup',
      icon: Server,
      color: 'from-amber-500 to-orange-700',
      price: 'Contact for Quote',
      features: ['Security Audit', 'Migration Strategy', 'Cost Optimization', '24/7 Support']
    }
  ];

  return (
    <div className="bg-[#f8fafc] dark:bg-[#001226] min-h-screen transition-colors duration-500">
      <section className="bg-white dark:bg-[#001a35] border-b border-slate-100 dark:border-white/5 py-20 overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-full mb-6">
              <Tag size={16} />
              <span className="text-xs font-semibold uppercase tracking-widest">Limited Time Deals</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-semibold text-[#4F46E5] dark:text-white mb-6">Exclusive Digital Offers</h1>
            <p className="text-slate-500 dark:text-slate-400 text-lg max-w-3xl mx-auto leading-relaxed">
              Accelerate your digital transformation with our premium services at unbeatable prices. Tailored for growth-focused businesses.
            </p>
          </motion.div>
        </div>

        {/* Animated Background Icons */}
        <div className="absolute top-20 left-10 opacity-5 -rotate-12 animate-pulse">
           <Zap size={120} />
        </div>
        <div className="absolute bottom-10 right-10 opacity-5 rotate-12 animate-pulse">
           <Gift size={120} />
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {activeOffers.map((offer, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-white dark:bg-[#001a35] rounded-[3rem] overflow-hidden shadow-sm border border-slate-100 dark:border-white/10 hover:shadow-2xl transition-all flex flex-col group"
              >
                <div className={`p-8 bg-gradient-to-br ${offer.color} text-white relative`}>
                  <div className="absolute top-6 right-6 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-xs font-semibold border border-white/30">
                    {offer.discount}
                  </div>
                  <offer.icon size={48} className="mb-6 group-hover:scale-110 transition-transform" />
                  <h3 className="text-2xl font-semibold mb-2">{offer.title}</h3>
                  <p className="text-white/80 text-sm leading-relaxed">{offer.desc}</p>
                </div>
                
                <div className="p-8 flex-grow">
                  <div className="space-y-4 mb-8">
                    {offer.features.map((feature, j) => (
                      <div key={j} className="flex items-center gap-3">
                        <CheckCircle size={18} className="text-emerald-500 shrink-0" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-auto">
                    <div className="text-lg font-semibold text-[#4F46E5] dark:text-white mb-6">
                      {offer.price}
                    </div>
                    <Link to="/quote" className="w-full flex items-center justify-center gap-2 py-4 bg-blue-600 text-white rounded-2xl font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 group/btn text-center">
                      Claim This Offer
                      <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Individual Service Mini Offers */}
          <div className="mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
             {[
               { name: 'Domain Registration', icon: Globe, detail: 'Get .in domains starting at just ₹499/yr' },
               { name: 'Business Emails', icon: Mail, detail: 'Professional emails for your team at ₹50/mo' },
               { name: 'Hosting Starter', icon: Server, detail: 'Lightning fast SSD hosting from ₹199/mo' },
               { name: 'Web Maintenance', icon: Code, detail: 'Keep your site updated for ₹2999/mo' }
             ].map((item, i) => (
               <div key={i} className="bg-white dark:bg-[#001a35] p-6 rounded-3xl border border-slate-100 dark:border-white/10 hover:border-blue-400 transition-all group">
                  <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:text-white transition-all">
                    <item.icon size={24} />
                  </div>
                  <h4 className="text-sm font-semibold text-[#4F46E5] dark:text-white mb-2">{item.name}</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mb-4">{item.detail}</p>
                  <Link to="/quote" className="text-blue-600 text-[10px] font-semibold uppercase tracking-widest flex items-center gap-1 hover:gap-2 transition-all">
                    Enquire Now <ChevronRight size={12} />
                  </Link>
               </div>
             ))}
          </div>
        </div>
      </section>

      {/* Countdown CTA */}
      <section className="py-24 bg-[#4F46E5] relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
           <div className="flex items-center justify-center gap-3 text-amber-400 mb-6">
              <Clock size={24} className="animate-pulse" />
              <span className="text-lg font-semibold tracking-widest uppercase">Offers Ending Soon!</span>
           </div>
           <h2 className="text-3xl md:text-5xl font-semibold text-white mb-8 leading-tight">Don't Miss Out on Your Digital Growth</h2>
           <p className="text-blue-200 text-lg mb-10 leading-relaxed">
             Join 100+ businesses that have successfully scaled with our expertise. These prices are valid for this month only.
           </p>
           <Link to="/quote" className="inline-block px-12 py-5 bg-[#3739dd] text-[#fff] rounded-2xl font-semibold text-lg hover:scale-105 transition-all shadow-xl shadow-yellow-500/20 text-center">
             Talk to Our Experts Now
           </Link>
        </div>
      </section>
    </div>
  );
};

export default Offers;
